﻿import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import Constant from 'utils/constants';
import { getErrorMessage } from 'helpers/errorHandling';
import { useGetProfile, useUpdateUser } from 'features/users/user.query';

import ErrorBoundary from 'components/ErrorBoundary';
import * as modalActions from 'components/Modal/Modal.ducks';
import Button from 'components/Button';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import Feedback from 'components/Feedback';
import Form from 'components/Form';

function RemoveClientModal({ modalId, clientId, onClientRemoved }) {
  const dispatch = useDispatch();

  const { data: profile, isLoading } = useGetProfile(clientId);
  const { mutate: updateUser, isLoading: isUpdating } = useUpdateUser();

  if (isLoading) {
    return (
      <Modal id={modalId} size="medium" title="Remove client" autoClose>
        <Loader />
      </Modal>
    );
  }

  // A failed profile request leaves data undefined once isLoading goes false, and
  // `profile.full_name` below then threw during this component's own render. The
  // ErrorBoundary underneath cannot catch that, because the error happens while
  // building the element the boundary would have wrapped, so React unmounted the
  // whole tree and client details went blank. Reachable with any bad client id.
  if (!profile) {
    return (
      <Modal id={modalId} size="medium" title="Remove client" autoClose>
        <Feedback
          type="error"
          message="We couldn't load this client, so they cannot be removed right now."
        />
      </Modal>
    );
  }

  return (
    <ErrorBoundary>
      <Modal id={modalId} size="medium" title="Remove client" autoClose>
        <Form
          title={`Are you sure you want to remove ${profile.full_name}?`}
          description="If you remove this client, you will no longer be able to recover
          it"
          onSubmit={() => {
            updateUser(
              {
                userId: clientId,
                payload: {
                  state: Constant.user.state.inactive,
                },
              },
              {
                onSuccess: () => {
                  toast.success('The client was removed successfully');
                  onClientRemoved?.();
                  dispatch(modalActions.setOpenModalId(null));
                },
                onError: (error) => {
                  toast.error(getErrorMessage(error));
                },
              },
            );
          }}
        >
          {() => (
            <Form.Actions>
              <Button disabled={isUpdating} type="submit" size="big">
                Remove client
              </Button>
            </Form.Actions>
          )}
        </Form>
      </Modal>
    </ErrorBoundary>
  );
}

RemoveClientModal.propTypes = {
  modalId: PropTypes.string.isRequired,
  clientId: PropTypes.number.isRequired,
  onClientRemoved: PropTypes.func,
};

export default RemoveClientModal;
